/**
 * Parse e-hentai / exhentai gallery list HTML into `Page<ComicCard>`.
 *
 * The site supports five list-display modes; we recognize each by its
 * containing CSS class:
 *
 *   - `<table class="itg gltc">` — Compact
 *   - `<table class="itg glte">` — Extended (default for new desktop accts)
 *   - `<div   class="itg gltm|gld">` — Thumbnail (default for mobile)
 *   - `<table class="itg gltl">` — Minimal / Minimal+
 *   - `<table class="itg gltL">` — older Minimal (single-table)
 *
 * Extended and Thumbnail are the two we handle explicitly because the
 * scaffold setting for the SPA's first-time visitor lands them there.
 * For everything else we fall back to a permissive "any anchor that links
 * to `/g/{gid}/{token}/`" pass that still produces usable cards (missing
 * tags and cover, but with title + gid).
 *
 * If the layout changes the heading-line we use, the parser will return
 * fewer items rather than throw — easier to triage than a hard fail.
 */

import { parseDocument, textOf, toInt, absoluteUrl } from '@/lib/html';
import { ParseError } from '@/lib/errors';
import type { ComicCard, Page, SourceId, Tag } from '@/sources/types';
import { publicEhImageUrl, SITE_E, SITE_EX, type SiteContext } from '../urls';

const GALLERY_URL_RE = /\/g\/(\d+)\/([0-9a-z]+)/i;
const PAGE_COUNT_RE = /(\d[\d,]*)\s+pages?/i;

export interface GalleryListContext {
  source: SourceId;
  /** Base URL used to resolve relative hrefs (typically the site host). */
  baseUrl: string;
  /**
   * Current page (0-indexed) as requested. Returned in the `Page<>`
   * envelope so the caller can compute the next-page cursor.
   */
  currentPage: number;
}

interface RawGalleryRow {
  gid: string;
  token: string;
  title: string;
  cover: string;
  category?: string;
  pageCount?: number;
  rating?: number;
  uploadedAt?: number;
  tags: Tag[];
}

const POSTED_DATE_RE = /(\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2})?)?)/;

function parsePosted(text: string): number | undefined {
  const t = text.trim();
  if (!t) return undefined;
  // Extract a `YYYY-MM-DD[ HH:MM[:SS]]` substring; ignore any "Posted: " /
  // "by: <uploader>" wrapping the site puts around it.
  const m = POSTED_DATE_RE.exec(t);
  if (!m || !m[1]) return undefined;
  const iso = m[1].replace(' ', 'T') + 'Z';
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : undefined;
}

function readGidFromHref(href: string): { gid: string; token: string } | null {
  const m = GALLERY_URL_RE.exec(href);
  if (!m || !m[1] || !m[2]) return null;
  return { gid: m[1], token: m[2] };
}

function pageCountFromAttrOrText(
  ...candidates: Array<string | null | undefined>
): number | undefined {
  for (const c of candidates) {
    if (!c) continue;
    const m = PAGE_COUNT_RE.exec(c);
    if (m && m[1]) {
      const n = toInt(m[1]);
      if (n > 0) return n;
    }
  }
  return undefined;
}

function ratingFromText(...candidates: Array<string | null | undefined>): number | undefined {
  for (const c of candidates) {
    if (!c) continue;
    const explicit = /(?:average|rating)\s*:?\s*([0-5](?:\.\d+)?)/i.exec(c);
    const bare = /^\s*([0-5](?:\.\d+)?)\s*(?:\/\s*5|stars?)?\s*$/i.exec(c);
    const value = explicit?.[1] ?? bare?.[1];
    if (!value) continue;
    const n = Number.parseFloat(value);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function ratingFromSpriteStyle(style: string | null | undefined): number | undefined {
  if (!style) return undefined;
  const shorthand = /background-position\s*:\s*(-?\d+)px\s+(-?\d+)px/i.exec(style);
  const xMatch = /background-position-x\s*:\s*(-?\d+)px/i.exec(style);
  const yMatch = /background-position-y\s*:\s*(-?\d+)px/i.exec(style);
  const x = shorthand?.[1] ?? xMatch?.[1];
  const y = shorthand?.[2] ?? yMatch?.[1];
  if (x === undefined || y === undefined) return undefined;

  const bucket = Math.round(Math.abs(Number.parseInt(x, 10)) / 16);
  const yOffset = Number.parseInt(y, 10);
  if (!Number.isFinite(bucket) || !Number.isFinite(yOffset)) return undefined;

  // E-Hentai's compact/thumbnail list can omit rating text and expose only
  // the star sprite. Top row is whole-star buckets (5,4,3...), second row is
  // half-star buckets (4.5,3.5,2.5...).
  const rating = yOffset <= -10 ? 4.5 - bucket : 5 - bucket;
  return rating >= 0.5 && rating <= 5 ? rating : undefined;
}

function ratingFromElement(root: ParentNode): number | undefined {
  const candidates = Array.from(
    root.querySelectorAll(
      '[title*="Average"], [title*="Rating"], [aria-label*="Average"], [aria-label*="Rating"], .ir',
    ),
  ) as HTMLElement[];
  for (const el of candidates) {
    const rating = ratingFromText(
      el.getAttribute('title'),
      el.getAttribute('aria-label'),
      el.getAttribute('data-rating'),
      textOf(el),
    );
    if (rating !== undefined) return rating;
    const spriteRating = ratingFromSpriteStyle(el.getAttribute('style'));
    if (spriteRating !== undefined) return spriteRating;
  }
  return undefined;
}

function categoryFromText(text: string): string | undefined {
  const t = text.trim();
  if (!t) return undefined;
  const KNOWN = [
    'Doujinshi',
    'Manga',
    'Artist CG',
    'Game CG',
    'Image Set',
    'Cosplay',
    'Asian Porn',
    'Misc',
    'Non-H',
    'Western',
  ];
  for (const k of KNOWN) {
    if (t.toLowerCase() === k.toLowerCase()) return k;
  }
  return t.length <= 24 ? t : undefined;
}

function siteFromContext(ctx: GalleryListContext): SiteContext {
  return ctx.source === 'exhentai' ? SITE_EX : SITE_E;
}

function coverUrl(raw: string, ctx: GalleryListContext): string {
  if (!raw) return '';
  return publicEhImageUrl(absoluteUrl(raw, ctx.baseUrl), siteFromContext(ctx));
}

function parseExtendedRows(doc: Document, ctx: GalleryListContext): RawGalleryRow[] {
  const out: RawGalleryRow[] = [];
  const rows = Array.from(doc.querySelectorAll('table.itg.glte > tbody > tr, table.glte tr'));
  for (const row of rows) {
    const linkAnchor = row.querySelector(
      'td.gl1e a, td.gl2e a[href*="/g/"], a[href*="/g/"]',
    ) as HTMLAnchorElement | null;
    const href = linkAnchor?.getAttribute('href') ?? '';
    const ids = readGidFromHref(href);
    if (!ids) continue;

    const img = row.querySelector('img') as HTMLImageElement | null;
    const cover = img?.getAttribute('data-src') ?? img?.getAttribute('src') ?? '';
    const title =
      textOf(row.querySelector('.glink')) ||
      textOf(linkAnchor?.querySelector('div')) ||
      textOf(linkAnchor);
    const categoryEl = row.querySelector('.cn, .cs, .ctd, .gl3e .cn');
    const category = categoryFromText(textOf(categoryEl));

    // The right-hand column in extended mode lists posted, length, uploader.
    // Each meta line is in its own `<div>`. Page count text is `"NNN pages"`.
    const metaDivs = Array.from(
      row.querySelectorAll('td.gl2e > div > div, td.gl2e div'),
    ) as HTMLElement[];
    let pageCount: number | undefined;
    let uploadedAt: number | undefined;
    const rating = ratingFromElement(row);
    for (const div of metaDivs) {
      const t = textOf(div);
      if (pageCount === undefined) {
        const pc = pageCountFromAttrOrText(t);
        if (pc !== undefined) pageCount = pc;
      }
      if (uploadedAt === undefined) {
        const p = parsePosted(t);
        if (p !== undefined) uploadedAt = p;
      }
    }

    const tagAnchors = Array.from(row.querySelectorAll('div.gt, .gtl')) as HTMLElement[];
    const tags: Tag[] = [];
    for (const t of tagAnchors) {
      const name = textOf(t);
      if (!name) continue;
      tags.push({ id: name, name });
    }

    const card: RawGalleryRow = {
      gid: ids.gid,
      token: ids.token,
      title,
      cover: coverUrl(cover, ctx),
      tags,
    };
    if (category) card.category = category;
    if (pageCount !== undefined) card.pageCount = pageCount;
    if (rating !== undefined) card.rating = rating;
    if (uploadedAt !== undefined) card.uploadedAt = uploadedAt;
    out.push(card);
  }
  return out;
}

function parseThumbnailCards(doc: Document, ctx: GalleryListContext): RawGalleryRow[] {
  const out: RawGalleryRow[] = [];
  const cards = Array.from(
    doc.querySelectorAll(
      'div.itg.gltm > div.gl1t, div.gltm > div.gl1t, div.itg.gld > div.gl1t, div.gld > div.gl1t',
    ),
  );
  for (const card of cards) {
    const a = card.querySelector('a[href*="/g/"]') as HTMLAnchorElement | null;
    const href = a?.getAttribute('href') ?? '';
    const ids = readGidFromHref(href);
    if (!ids) continue;

    const img = card.querySelector('img') as HTMLImageElement | null;
    const cover = img?.getAttribute('data-src') ?? img?.getAttribute('src') ?? '';
    const title = textOf(card.querySelector('.glink')) || (img?.getAttribute('title') ?? '').trim();
    const category = categoryFromText(textOf(card.querySelector('.cn, .cs, .ctd')));
    const dateText = textOf(card.querySelector('.gl4t'));
    const uploadedAt = parsePosted(dateText);
    const pageCount = pageCountFromAttrOrText(textOf(card.querySelector('.gl5t')), title);
    const rating = ratingFromElement(card);

    const row: RawGalleryRow = {
      gid: ids.gid,
      token: ids.token,
      title,
      cover: coverUrl(cover, ctx),
      tags: [],
    };
    if (category) row.category = category;
    if (pageCount !== undefined) row.pageCount = pageCount;
    if (rating !== undefined) row.rating = rating;
    if (uploadedAt !== undefined) row.uploadedAt = uploadedAt;
    out.push(row);
  }
  return out;
}

/**
 * Compact mode is the **default for anonymous users** on e-hentai's
 * frontend, so anyone hitting `/popular` or `/` without cookies lands here.
 * Structure (rough):
 *
 *   <table class="itg gltc">
 *     <tr>
 *       <td class="gl1c glcat"><div class="cn ct1">Misc</div></td>
 *       <td class="gl2c">
 *         <div class="glthumb"><div><img src="https://ehgt.org/.../...t.webp" .../></div></div>
 *         <div>… "98 pages" …</div>
 *         <div id="posted_NNN">2026-05-12 16:20</div>
 *       </td>
 *       <td class="gl3c glname">
 *         <a href="/g/{gid}/{token}/">
 *           <div class="glink">Title</div>
 *           <div><div class="gt" title="language:english">…</div>…</div>
 *         </a>
 *       </td>
 *     </tr>
 *
 * Cover lives in `td.gl2c`, link in `td.gl3c` — so the permissive
 * fallback that does `a.querySelector('img')` misses the cover. Hence
 * this dedicated parser.
 *
 * FRAGILE: assumes the `gl1c`/`gl2c`/`gl3c` column convention. Survives
 * heading shuffles, but a column-class rename will silently drop covers.
 */
function parseCompactRows(doc: Document, ctx: GalleryListContext): RawGalleryRow[] {
  const out: RawGalleryRow[] = [];
  const rows = Array.from(doc.querySelectorAll('table.itg.gltc > tbody > tr, table.gltc tr'));
  for (const row of rows) {
    const linkAnchor = row.querySelector('td.gl3c a[href*="/g/"]') as HTMLAnchorElement | null;
    const href = linkAnchor?.getAttribute('href') ?? '';
    const ids = readGidFromHref(href);
    if (!ids) continue;

    const img = row.querySelector('td.gl2c img') as HTMLImageElement | null;
    const cover = img?.getAttribute('data-src') ?? img?.getAttribute('src') ?? '';
    const title =
      textOf(linkAnchor?.querySelector('.glink')) ||
      (img?.getAttribute('title') ?? '').trim() ||
      (img?.getAttribute('alt') ?? '').trim() ||
      textOf(linkAnchor);

    const categoryEl = row.querySelector('td.gl1c .cn, td.gl1c.glcat .cn, td.gl1c.glcat');
    const category = categoryFromText(textOf(categoryEl));

    const gl2cText = textOf(row.querySelector('td.gl2c'));
    const pageCount = pageCountFromAttrOrText(gl2cText);
    const uploadedAt = parsePosted(gl2cText);
    const rating = ratingFromElement(row);

    const tagAnchors = Array.from(
      row.querySelectorAll('td.gl3c .gt, td.gl3c .gtl'),
    ) as HTMLElement[];
    const tags: Tag[] = [];
    for (const t of tagAnchors) {
      // The site puts the canonical namespace:tag in `title=…`; falls back to text.
      const titleAttr = t.getAttribute('title') ?? textOf(t);
      if (!titleAttr) continue;
      const colonIdx = titleAttr.indexOf(':');
      if (colonIdx > 0) {
        const ns = titleAttr.slice(0, colonIdx);
        const name = titleAttr.slice(colonIdx + 1);
        tags.push({ id: titleAttr, name, namespace: ns });
      } else {
        tags.push({ id: titleAttr, name: titleAttr });
      }
    }

    const card: RawGalleryRow = {
      gid: ids.gid,
      token: ids.token,
      title,
      cover: coverUrl(cover, ctx),
      tags,
    };
    if (category) card.category = category;
    if (pageCount !== undefined) card.pageCount = pageCount;
    if (rating !== undefined) card.rating = rating;
    if (uploadedAt !== undefined) card.uploadedAt = uploadedAt;
    out.push(card);
  }
  return out;
}

function parseFallbackAnchors(doc: Document, ctx: GalleryListContext): RawGalleryRow[] {
  const out: RawGalleryRow[] = [];
  const anchors = Array.from(doc.querySelectorAll('a[href*="/g/"]')) as HTMLAnchorElement[];
  const seen = new Set<string>();
  for (const a of anchors) {
    const href = a.getAttribute('href') ?? '';
    const ids = readGidFromHref(href);
    if (!ids) continue;
    const dedupKey = `${ids.gid}|${ids.token}`;
    if (seen.has(dedupKey)) continue;
    seen.add(dedupKey);

    const innerImg = a.querySelector('img') as HTMLImageElement | null;
    const cover = innerImg?.getAttribute('src') ?? '';
    let title =
      textOf(a.querySelector('.glink')) ||
      textOf(a.querySelector('div')) ||
      (innerImg?.getAttribute('title') ?? '').trim() ||
      (innerImg?.getAttribute('alt') ?? '').trim() ||
      textOf(a);
    if (!title) title = `Gallery ${ids.gid}`;
    out.push({
      gid: ids.gid,
      token: ids.token,
      title,
      cover: coverUrl(cover, ctx),
      tags: [],
    });
  }
  return out;
}

function dedupe(rows: RawGalleryRow[]): RawGalleryRow[] {
  const seen = new Set<string>();
  const out: RawGalleryRow[] = [];
  for (const row of rows) {
    const key = `${row.gid}|${row.token}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

function readNextCursor(doc: Document): string | undefined {
  const nextHref =
    (doc.querySelector('#unext[href]') as HTMLAnchorElement | null)?.getAttribute('href') ??
    undefined;
  const nextUrlMatch = /var\s+nexturl\s*=\s*"([^"]+)"/.exec(doc.documentElement.outerHTML);
  const raw = nextHref || nextUrlMatch?.[1];
  if (!raw) return undefined;

  try {
    const next = new URL(raw, 'https://e-hentai.org/');
    return next.searchParams.get('next') ?? undefined;
  } catch {
    return undefined;
  }
}

function readHasMore(doc: Document): {
  hasMore: boolean;
  totalPages?: number;
  nextCursor?: string;
} {
  const nextCursor = readNextCursor(doc);
  if (nextCursor) {
    return { hasMore: true, nextCursor };
  }

  // Older pagination table `<table class="ptt">` holds the page count.
  // `<a>` children with text "Next>" / ">" indicate more pages.
  const ptt = doc.querySelector('table.ptt');
  if (!ptt) return { hasMore: false };
  let max = 1;
  let hasNext = false;
  const tds = Array.from(ptt.querySelectorAll('td'));
  for (const td of tds) {
    const t = (td.textContent ?? '').trim();
    const n = Number.parseInt(t, 10);
    if (Number.isFinite(n) && n > max) max = n;
    if (/^next/i.test(t) || /^>$/.test(t)) hasNext = true;
  }
  const result: { hasMore: boolean; totalPages?: number; nextCursor?: string } = {
    hasMore: hasNext,
  };
  if (max > 1) result.totalPages = max;
  return result;
}

function toCard(row: RawGalleryRow, source: SourceId): ComicCard {
  // `gid:token` is the canonical e-hentai gallery identifier; both halves
  // are required to construct the detail / page URLs, and the token never
  // changes for a given gallery so it's safe to bake into the persistent ID.
  const card: ComicCard = {
    id: `${row.gid}:${row.token}`,
    source,
    title: row.title || `Gallery ${row.gid}`,
    coverUrl: row.cover,
  };
  if (row.pageCount !== undefined) card.pageCount = row.pageCount;
  if (row.rating !== undefined) card.rating = row.rating;
  if (row.uploadedAt !== undefined) card.uploadedAt = row.uploadedAt;
  const tags: Tag[] = [...row.tags];
  if (row.category)
    tags.unshift({
      id: `category:${row.category.toLowerCase()}`,
      name: row.category,
      namespace: 'category',
    });
  tags.push({ id: `token:${row.token}`, name: row.token, namespace: 'token' });
  card.tags = tags;
  return card;
}

/**
 * Main entry — turn the full HTML of a list URL into a `Page<ComicCard>`.
 *
 * The `currentPage` (in ctx) round-trips into the result so the caller can
 * keep a stable cursor without re-parsing.
 */
export function parseGalleryList(html: string, ctx: GalleryListContext): Page<ComicCard> {
  if (!html) throw new ParseError('parseGalleryList: empty html', { source: ctx.source });
  const doc = parseDocument(html);

  // Try each layout in order; concatenate so a mixed page (rare but possible)
  // doesn't drop items. Compact is anonymous-user default on e-hentai so
  // it sits early in the dispatch; the permissive anchor-fallback stays
  // last as a "produce SOMETHING usable" guard.
  let rows = parseExtendedRows(doc, ctx);
  if (rows.length === 0) rows = parseThumbnailCards(doc, ctx);
  if (rows.length === 0) rows = parseCompactRows(doc, ctx);
  if (rows.length === 0) rows = parseFallbackAnchors(doc, ctx);
  rows = dedupe(rows);

  const { hasMore, totalPages, nextCursor } = readHasMore(doc);

  const page: Page<ComicCard> = {
    items: rows.map((r) => toCard(r, ctx.source)),
    page: ctx.currentPage,
    hasMore,
  };
  if (nextCursor !== undefined) page.nextCursor = nextCursor;
  if (totalPages !== undefined) page.totalPages = totalPages;
  if (nextCursor !== undefined) page.nextCursor = nextCursor;
  return page;
}
